package com.evoapps.evoshift.ui.billing

import com.evoapps.evoshift.ui.util.dayAndMonth
import com.evoapps.evoshift.ui.util.onDayAndDate
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.WorkspacePremium
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.billing.PlanChange
import com.evoapps.evoshift.domain.billing.PlanOffer
import com.evoapps.evoshift.domain.billing.OwnerPlan
import com.evoapps.evoshift.domain.billing.PlanTier
import com.evoapps.evoshift.domain.billing.Plans
import com.evoapps.evoshift.ui.CardBlue
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass
import com.evoapps.evoshift.ui.adaptive.TwoPane
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.legal.LegalLinks
import com.evoapps.evoshift.ui.util.TestTags
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.PlanChoiceState
import com.evoapps.evoshift.widgets.GoldButton
import com.evoapps.evoshift.widgets.StandoutCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.paywall_already_premium
import evoshift.composeapp.generated.resources.paywall_choose_period
import evoshift.composeapp.generated.resources.paywall_choose_seats
import evoshift.composeapp.generated.resources.paywall_change_current_plan
import evoshift.composeapp.generated.resources.paywall_change_now_notice
import evoshift.composeapp.generated.resources.paywall_change_open_store
import evoshift.composeapp.generated.resources.paywall_change_other_store_notice
import evoshift.composeapp.generated.resources.paywall_change_plan
import evoshift.composeapp.generated.resources.paywall_change_portal_notice
import evoshift.composeapp.generated.resources.paywall_change_promo_notice
import evoshift.composeapp.generated.resources.paywall_change_renewal_notice
import evoshift.composeapp.generated.resources.paywall_change_renewal_notice_undated
import evoshift.composeapp.generated.resources.paywall_current_badge
import evoshift.composeapp.generated.resources.paywall_current_plan_notice
import evoshift.composeapp.generated.resources.paywall_included_money
import evoshift.composeapp.generated.resources.paywall_included_scheduling
import evoshift.composeapp.generated.resources.paywall_included_team
import evoshift.composeapp.generated.resources.paywall_included_team_free
import evoshift.composeapp.generated.resources.paywall_included_workplaces
import evoshift.composeapp.generated.resources.paywall_team_size
import evoshift.composeapp.generated.resources.paywall_trial_ended
import evoshift.composeapp.generated.resources.paywall_trial_offer
import evoshift.composeapp.generated.resources.paywall_trial_running
import evoshift.composeapp.generated.resources.paywall_compare_title
import evoshift.composeapp.generated.resources.paywall_no_offers
import evoshift.composeapp.generated.resources.paywall_not_configured_desc
import evoshift.composeapp.generated.resources.paywall_not_configured_title
import evoshift.composeapp.generated.resources.paywall_offers_error
import evoshift.composeapp.generated.resources.paywall_restore
import evoshift.composeapp.generated.resources.paywall_retry
import evoshift.composeapp.generated.resources.paywall_start_trial
import evoshift.composeapp.generated.resources.paywall_subscribe
import evoshift.composeapp.generated.resources.paywall_subtitle
import evoshift.composeapp.generated.resources.paywall_terms
import evoshift.composeapp.generated.resources.paywall_title
import androidx.compose.ui.platform.LocalUriHandler
import evoshift.composeapp.generated.resources.over_plan_body
import evoshift.composeapp.generated.resources.over_plan_body_no_bigger
import evoshift.composeapp.generated.resources.over_plan_renewal
import evoshift.composeapp.generated.resources.over_plan_title
import evoshift.composeapp.generated.resources.paywall_grow_freely
import evoshift.composeapp.generated.resources.paywall_hide_other_plans
import evoshift.composeapp.generated.resources.paywall_larger_team
import evoshift.composeapp.generated.resources.paywall_larger_team_desc
import evoshift.composeapp.generated.resources.paywall_other_plans
import evoshift.composeapp.generated.resources.paywall_recommended
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/** A text action under the plans: "Continue with the free trial" in onboarding, "Not now" on the paywall. */
data class PlanSecondaryAction(val label: String, val onClick: () -> Unit)

/**
 * The plan choice on navy (SH-38, SH-52), EVoLabels' PlansContent in EVoShift's colours: the
 * promise, the free months, what every plan includes, then the seat tiers of the RevenueCat
 * offering and their periods with the store's prices, one gold action.
 * Stateless, so the onboarding step and the paywall screen share it and tests drive it.
 * Side by side on wide windows, one column on phones.
 */
@Composable
fun PlanChoiceContent(
    state: PlanChoiceState,
    onSelect: (String) -> Unit,
    onSelectSeats: (Int) -> Unit,
    onSubscribe: () -> Unit,
    onRestore: () -> Unit,
    onRetry: () -> Unit,
    secondaryAction: PlanSecondaryAction?,
    modifier: Modifier = Modifier,
    title: String? = null,
    onToggleOtherPlans: () -> Unit = {}
) {
    val showcase: @Composable () -> Unit = {
        Column(verticalArrangement = Arrangement.spacedBy(20.dp), horizontalAlignment = Alignment.CenterHorizontally) {
            PlanHero(title)
            TrialNotice(state.plan)
            PlanIncluded()
        }
    }
    val purchase: @Composable () -> Unit = {
        PlanPurchasePanel(state, onSelect, onSelectSeats, onSubscribe, onRestore, onRetry, secondaryAction, onToggleOtherPlans)
    }
    if (LocalWindowWidthClass.current.isExpanded) {
        TwoPane(modifier = modifier.fillMaxWidth(), first = { showcase() }, second = { purchase() }, firstWeight = 0.48f, spacing = 32.dp)
    } else {
        Column(modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(20.dp)) {
            showcase()
            purchase()
        }
    }
}

@Composable
private fun PlanHero(title: String?) {
    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Box(
            Modifier.size(72.dp).background(GoldMetallicBrush, CircleShape),
            contentAlignment = Alignment.Center
        ) {
            Icon(Icons.Default.WorkspacePremium, contentDescription = null, tint = DeepNavy, modifier = Modifier.size(40.dp))
        }
        Text(
            title ?: stringResource(Res.string.paywall_title),
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold,
            color = LightGold,
            textAlign = TextAlign.Center
        )
        Text(
            stringResource(Res.string.paywall_subtitle),
            style = MaterialTheme.typography.bodyLarge,
            color = Color.White.copy(alpha = 0.8f),
            textAlign = TextAlign.Center
        )
    }
}

/** What every plan includes: plans differ only in seats, so this lists the app, not limits. */
@Composable
private fun PlanIncluded() {
    StandoutCard(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text(
                stringResource(Res.string.paywall_compare_title),
                style = MaterialTheme.typography.titleSmall,
                color = LightGold,
                fontWeight = FontWeight.Bold
            )
            listOf(
                stringResource(Res.string.paywall_included_scheduling),
                stringResource(Res.string.paywall_included_team),
                stringResource(Res.string.paywall_included_money),
                stringResource(Res.string.paywall_included_workplaces, Plans.MAX_WORKPLACES.toString()),
                stringResource(Res.string.paywall_included_team_free)
            ).forEach { line ->
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    Icon(Icons.Default.CheckCircle, null, tint = LightGold, modifier = Modifier.size(18.dp))
                    Text(line, color = Color.White.copy(alpha = 0.85f), style = MaterialTheme.typography.bodyMedium)
                }
            }
        }
    }
}

/** The free months: on offer before the first workplace, their end date while they run, or over. */
@Composable
private fun TrialNotice(plan: OwnerPlan?) {
    val text = when {
        plan == null || plan.isPaid -> return
        plan.tier == PlanTier.NONE -> stringResource(Res.string.paywall_trial_ended)
        plan.trialEndsAt == null -> stringResource(Res.string.paywall_trial_offer, trialLengthLabel())
        else -> stringResource(
            Res.string.paywall_trial_running,
            onDayAndDate(plan.trialEndsAt!!.toLocalDateTime(TimeZone.currentSystemDefault()).date)
        )
    }
    Surface(
        shape = RoundedCornerShape(12.dp),
        color = Color.White.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, if (plan.tier == PlanTier.NONE) Color(0xFFFFB4AB) else LightGold.copy(alpha = 0.6f)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            text,
            Modifier.padding(12.dp),
            color = Color.White,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.SemiBold,
            textAlign = TextAlign.Center
        )
    }
}

@Composable
private fun PlanPurchasePanel(
    state: PlanChoiceState,
    onSelect: (String) -> Unit,
    onSelectSeats: (Int) -> Unit,
    onSubscribe: () -> Unit,
    onRestore: () -> Unit,
    onRetry: () -> Unit,
    secondaryAction: PlanSecondaryAction?,
    onToggleOtherPlans: () -> Unit
) {
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp), horizontalAlignment = Alignment.CenterHorizontally) {
        when {
            !state.isBillingConfigured -> NoticeCard(
                icon = { Icon(Icons.Default.Info, null, tint = LightGold, modifier = Modifier.size(32.dp)) },
                title = stringResource(Res.string.paywall_not_configured_title),
                body = stringResource(Res.string.paywall_not_configured_desc)
            )

            state.isLoadingOffers -> CircularProgressIndicator(color = DarkGold, modifier = Modifier.padding(24.dp))

            state.offersError != null -> {
                Text(
                    "${stringResource(Res.string.paywall_offers_error)} ${state.offersError.asString()}",
                    color = Color.White.copy(alpha = 0.85f),
                    textAlign = TextAlign.Center
                )
                TextButton(onClick = onRetry, modifier = Modifier.testTag(TestTags.PAYWALL_RETRY)) {
                    Text(stringResource(Res.string.paywall_retry), color = LightGold, fontWeight = FontWeight.Bold)
                }
            }

            state.offers.isEmpty() -> Text(
                stringResource(Res.string.paywall_no_offers),
                color = Color.White.copy(alpha = 0.85f),
                textAlign = TextAlign.Center
            )

            else -> {
                if (state.plan?.isPaid == true) {
                    NoticeCard(
                        icon = { Icon(Icons.Default.CheckCircle, null, tint = LightGold, modifier = Modifier.size(32.dp)) },
                        title = stringResource(Res.string.paywall_already_premium),
                        body = stringResource(Res.string.paywall_current_plan_notice, state.plan.seats.toString())
                    )
                }
                if (state.isOverPlan) OverPlanNotice(state)
                state.seatsInUse?.let {
                    Text(
                        pluralStringResource(Res.plurals.paywall_team_size, it, it),
                        style = MaterialTheme.typography.bodySmall,
                        color = Color.White.copy(alpha = 0.7f),
                        modifier = Modifier.fillMaxWidth()
                    )
                }
                if (state.isRecommendedSelected) {
                    Text(
                        stringResource(Res.string.paywall_recommended),
                        style = MaterialTheme.typography.titleSmall,
                        color = LightGold,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.fillMaxWidth().testTag(TestTags.PAYWALL_RECOMMENDED)
                    )
                }
                state.error?.let {
                    Text(it.asString(), color = Color(0xFFFFB4AB), style = MaterialTheme.typography.bodyMedium, textAlign = TextAlign.Center)
                }
                val selected = state.selectedOffer
                val trial = selected?.introductoryOffer?.isFreeTrial == true
                // SH-220: ONE primary action with the store's own price ("Switch to the plan for
                // up to 10 people at 39 zł a month"); the other tiers and periods sit under it.
                GoldButton(
                    text = when (state.change) {
                        PlanChange.CURRENT -> stringResource(Res.string.paywall_change_current_plan)
                        PlanChange.IN_OTHER_STORE -> stringResource(Res.string.paywall_change_open_store)
                        PlanChange.NEW_PLAN, null -> if (trial || selected == null) {
                            stringResource(if (trial) Res.string.paywall_start_trial else Res.string.paywall_subscribe)
                        } else selected.goToPlanLabel()
                        PlanChange.UPGRADE_NOW, PlanChange.AT_RENEWAL, PlanChange.IN_WEB_PORTAL ->
                            selected?.goToPlanLabel() ?: stringResource(Res.string.paywall_change_plan)
                    },
                    onClick = onSubscribe,
                    enabled = state.canSubscribe && !state.isRestoring,
                    isBusy = state.isPurchasing,
                    modifier = Modifier.fillMaxWidth().testTag(TestTags.PAYWALL_SUBSCRIBE)
                )
                selected?.introThenLabel()?.let {
                    Text(it, style = MaterialTheme.typography.bodySmall, color = LightGold, textAlign = TextAlign.Center, modifier = Modifier.fillMaxWidth())
                }
                PlanChangeNotice(state)
                if (state.plan?.isPaid != true) {
                    Text(
                        stringResource(Res.string.paywall_grow_freely),
                        style = MaterialTheme.typography.bodySmall,
                        color = Color.White.copy(alpha = 0.7f),
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
                if (state.recommendedOffer != null) {
                    TextButton(
                        onClick = onToggleOtherPlans,
                        enabled = !state.isBusy,
                        modifier = Modifier.testTag(TestTags.PAYWALL_OTHER_PLANS)
                    ) {
                        Text(
                            stringResource(if (state.showOtherPlans) Res.string.paywall_hide_other_plans else Res.string.paywall_other_plans),
                            color = LightGold,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
                if (state.otherPlansShown) OtherPlans(state, onSelect, onSelectSeats)
                LargerTeamEntry(state)
                Text(
                    stringResource(Res.string.paywall_terms),
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.White.copy(alpha = 0.6f),
                    textAlign = TextAlign.Center
                )
                // Apple 3.1.2: privacy policy and terms of use next to the purchase (SH-50).
                LegalLinks(color = LightGold)
                TextButton(onClick = onRestore, enabled = !state.isBusy, modifier = Modifier.testTag(TestTags.PAYWALL_RESTORE)) {
                    if (state.isRestoring) CircularProgressIndicator(Modifier.size(16.dp), color = LightGold, strokeWidth = 2.dp)
                    else Text(stringResource(Res.string.paywall_restore), color = LightGold)
                }
            }
        }
        state.message?.let {
            Text(it.asString(), color = Color.White.copy(alpha = 0.85f), style = MaterialTheme.typography.bodyMedium, textAlign = TextAlign.Center)
        }
        if (secondaryAction != null) {
            TextButton(
                onClick = secondaryAction.onClick,
                enabled = !state.isPurchasing,
                modifier = Modifier.testTag(TestTags.PAYWALL_SECONDARY)
            ) {
                Text(secondaryAction.label, color = Color.White, fontWeight = FontWeight.SemiBold)
            }
        }
    }
}

/** SH-220: "Other plans": every tier of the offering and the periods of the chosen one. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun OtherPlans(state: PlanChoiceState, onSelect: (String) -> Unit, onSelectSeats: (Int) -> Unit) {
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text(
            stringResource(Res.string.paywall_choose_seats),
            style = MaterialTheme.typography.titleSmall,
            color = LightGold,
            modifier = Modifier.fillMaxWidth()
        )
        FlowRow(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            state.seatTiers.forEach { seats ->
                SeatTierChip(
                    seats = seats,
                    isCurrent = seats == state.currentSeats,
                    selected = seats == state.selectedOffer?.seats,
                    enabled = !state.isBusy && state.fitsTeam(seats),
                    onClick = { onSelectSeats(seats) }
                )
            }
        }
        Text(
            stringResource(Res.string.paywall_choose_period),
            style = MaterialTheme.typography.titleSmall,
            color = LightGold,
            modifier = Modifier.fillMaxWidth()
        )
        state.offersOfSelectedTier.forEach { offer ->
            OfferCard(
                offer,
                selected = offer.packageId == state.selectedPackageId,
                enabled = !state.isBusy,
                isCurrent = offer.productId == state.currentProductId
            ) { onSelect(offer.packageId) }
        }
    }
}

/**
 * SH-220: the team grew past the plan. Nobody was blocked; the next tier is proposed (stores
 * charge more only once the customer confirms), with the renewal date when it is close.
 */
@Composable
private fun OverPlanNotice(state: PlanChoiceState) {
    val team = state.seatsInUse ?: return
    val seats = state.currentSeats ?: return
    val next = state.recommendedOffer?.seats
    Surface(
        shape = RoundedCornerShape(12.dp),
        color = Color.White.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, LightGold.copy(alpha = 0.6f)),
        modifier = Modifier.fillMaxWidth().testTag(TestTags.PAYWALL_OVER_PLAN)
    ) {
        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            if (next != null) {
                Text(
                    stringResource(Res.string.over_plan_title, upToPeopleLabel(next)),
                    color = LightGold,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold
                )
            }
            Text(
                stringResource(
                    if (next != null) Res.string.over_plan_body else Res.string.over_plan_body_no_bigger,
                    peopleCountLabel(team),
                    upToPeopleLabel(seats)
                ),
                color = Color.White,
                style = MaterialTheme.typography.bodyMedium
            )
            state.plan?.storePlan?.entitlement?.expiresAt?.let { end ->
                Text(
                    stringResource(Res.string.over_plan_renewal, dayAndMonth(end.toLocalDateTime(TimeZone.currentSystemDefault()).date)),
                    color = Color.White.copy(alpha = 0.85f),
                    style = MaterialTheme.typography.bodySmall
                )
            }
        }
    }
}

/**
 * SH-220: no tier above the offering's biggest is ever listed; a larger team writes to us
 * ([Plans.SALES_CONTACT_EMAIL]). Leads when the team is already bigger than every tier.
 */
@Composable
private fun LargerTeamEntry(state: PlanChoiceState) {
    val biggest = state.biggestTier ?: return
    val uriHandler = LocalUriHandler.current
    Column(
        Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(2.dp)
    ) {
        TextButton(
            onClick = { runCatching { uriHandler.openUri("mailto:${Plans.SALES_CONTACT_EMAIL}") } },
            modifier = Modifier.testTag(TestTags.PAYWALL_CONTACT)
        ) {
            Text(
                stringResource(Res.string.paywall_larger_team),
                color = LightGold,
                fontWeight = if (state.isTeamBeyondTiers) FontWeight.Bold else FontWeight.SemiBold
            )
        }
        Text(
            stringResource(Res.string.paywall_larger_team_desc, upToPeopleLabel(biggest)),
            style = MaterialTheme.typography.bodySmall,
            color = Color.White.copy(alpha = 0.7f),
            textAlign = TextAlign.Center
        )
    }
}

/** When and where the selected change applies (SH-52): now, at the renewal, in the portal or another store. */
@Composable
private fun PlanChangeNotice(state: PlanChoiceState) {
    val text = when (state.change) {
        PlanChange.UPGRADE_NOW -> stringResource(Res.string.paywall_change_now_notice)
        PlanChange.AT_RENEWAL -> state.plan?.storePlan?.entitlement?.expiresAt?.let { end ->
            stringResource(Res.string.paywall_change_renewal_notice, dayAndMonth(end.toLocalDateTime(TimeZone.currentSystemDefault()).date))
        } ?: stringResource(Res.string.paywall_change_renewal_notice_undated)
        PlanChange.IN_WEB_PORTAL -> stringResource(Res.string.paywall_change_portal_notice)
        PlanChange.IN_OTHER_STORE -> stringResource(Res.string.paywall_change_other_store_notice)
        PlanChange.NEW_PLAN -> if (state.isOnGrantedPlan) stringResource(Res.string.paywall_change_promo_notice) else return
        PlanChange.CURRENT, null -> return
    }
    Text(
        text,
        Modifier.fillMaxWidth().testTag(TestTags.PAYWALL_CHANGE_NOTICE),
        color = Color.White.copy(alpha = 0.85f),
        style = MaterialTheme.typography.bodyMedium,
        textAlign = TextAlign.Center
    )
}

@Composable
private fun SeatTierChip(seats: Int, isCurrent: Boolean, selected: Boolean, enabled: Boolean, onClick: () -> Unit) {
    Surface(
        shape = RoundedCornerShape(12.dp),
        color = if (selected) CardBlue else Color.White.copy(alpha = 0.06f),
        border = if (selected) BorderStroke(2.dp, GoldMetallicBrush) else BorderStroke(1.dp, Color.White.copy(alpha = if (enabled) 0.3f else 0.1f)),
        modifier = Modifier
            .handCursor()
            .selectable(selected = selected, enabled = enabled, role = Role.RadioButton, onClick = onClick)
            .testTag(TestTags.seatTier(seats))
    ) {
        Row(
            Modifier.padding(horizontal = 14.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Text(
                seatsLabel(seats),
                color = Color.White.copy(alpha = if (enabled) 1f else 0.4f),
                fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal,
                style = MaterialTheme.typography.bodyMedium
            )
            if (isCurrent) {
                Text(stringResource(Res.string.paywall_current_badge), style = MaterialTheme.typography.labelSmall, color = LightGold)
            }
        }
    }
}

@Composable
private fun OfferCard(offer: PlanOffer, selected: Boolean, enabled: Boolean, isCurrent: Boolean, onClick: () -> Unit) {
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = if (selected) CardBlue else Color.White.copy(alpha = 0.06f),
        border = if (selected) BorderStroke(2.dp, GoldMetallicBrush) else BorderStroke(1.dp, Color.White.copy(alpha = 0.2f)),
        modifier = Modifier
            .fillMaxWidth()
            .handCursor()
            .selectable(selected = selected, enabled = enabled, role = Role.RadioButton, onClick = onClick)
            .testTag(TestTags.offer(offer.packageId))
    ) {
        Row(Modifier.padding(horizontal = 12.dp, vertical = 12.dp), verticalAlignment = Alignment.CenterVertically) {
            RadioButton(
                selected = selected,
                onClick = null,
                colors = RadioButtonDefaults.colors(selectedColor = LightGold, unselectedColor = Color.White.copy(alpha = 0.6f))
            )
            Spacer(Modifier.width(8.dp))
            Column(Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(offer.period.label(), style = MaterialTheme.typography.titleMedium, color = Color.White, fontWeight = FontWeight.Bold)
                    if (isCurrent) {
                        Text(stringResource(Res.string.paywall_current_badge), style = MaterialTheme.typography.labelSmall, color = LightGold)
                    }
                }
                offer.introLabel()?.let {
                    Text(it, style = MaterialTheme.typography.bodySmall, color = LightGold)
                }
            }
            Text(offer.priceLabel(), style = MaterialTheme.typography.bodyLarge, color = Color.White, fontWeight = FontWeight.Bold, textAlign = TextAlign.End)
        }
    }
}

@Composable
private fun NoticeCard(icon: @Composable () -> Unit, title: String, body: String) {
    StandoutCard(Modifier.fillMaxWidth()) {
        Column(
            Modifier.fillMaxWidth().padding(20.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            icon()
            Text(title, style = MaterialTheme.typography.titleMedium, color = LightGold, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
            Text(body, style = MaterialTheme.typography.bodyMedium, color = Color.White.copy(alpha = 0.85f), textAlign = TextAlign.Center)
        }
    }
}
